import { useTranslation } from "react-i18next";

import type { RecruitNowWebhookOverviewItem } from "../api/types";

import Icons from "@/components/icons";
import { Badge } from "@/components/ui/v2/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/v2/tooltip";
import { TableColumn } from "@/types/table/tableProps";

export function useWebhookOverviewTableColumns(): TableColumn<RecruitNowWebhookOverviewItem>[] {
  const { t } = useTranslation();

  return [
    {
      width: "25%",
      header: t("common.clientName"),
      accessor: "Client.Name",
      Cell: (item) => (
        <span className="font-medium">
          {item.clientName}
        </span>
      ),
    },
    {
      width: "25%",
      header: t("webhooks.recruitnow.webhookId"),
      accessor: "cockpitWebhookId",
      Cell: (item) =>
        item.cockpitWebhookId ? (
          <span className="font-mono text-sm">{item.cockpitWebhookId}</span>
        ) : (
          <span className="text-muted-foreground">{t("common.none")}</span>
        ),
    },
    {
      width: "10%",
      header: t("webhooks.recruitnow.registeredCount"),
      accessor: "registeredWebhookCount",
      Cell: (item) => <span className="text-sm">{item.registeredWebhookCount}</span>,
    },
    {
      width: "10%",
      header: t("common.status"),
      accessor: "status",
      Cell: (item) => {
        // Check module status first - if module is inactive, show inactive status regardless of webhook registration
        if (item.isModuleActive === false) {
          return (
            <Badge variant="neutral" size="medium">
              {t("common.inactive")}
            </Badge>
          );
        }

        // Client has no webhook registration
        if (!item.cockpitWebhookId) {
          return (
            <Badge variant="outline" size="medium">
              {t("webhooks.recruitnow.status.noRegistration")}
            </Badge>
          );
        }

        // Check for errors first - if we have error data, show error regardless of count
        const hasError =
          (item.webhookErrorState && item.webhookErrorState !== "None") ||
          item.webhookErrorMessage;
        if (hasError) {
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Badge variant="warning" size="medium">
                    <Icons.WarningIcon fontSize={16} />
                    {t("common.error")}
                  </Badge>
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-md">
                <div className="flex flex-col gap-2">
                  {item.webhookErrorState && item.webhookErrorState !== "None" && (
                    <div>
                      <span className="font-semibold">
                        {t("webhooks.recruitnow.errorState")}:{" "}
                      </span>
                      <span>{item.webhookErrorState}</span>
                    </div>
                  )}
                  {item.webhookErrorMessage && (
                    <div>
                      <span className="font-semibold">
                        {t("webhooks.recruitnow.errorMessage")}:{" "}
                      </span>
                      <span>{item.webhookErrorMessage}</span>
                    </div>
                  )}
                  {item.isSecretMismatched && (
                    <div>{t("webhooks.recruitnow.secretMismatch")}</div>
                  )}
                  {item.isUriMismatched && (
                    <div>{t("webhooks.recruitnow.uriMismatch")}</div>
                  )}
                  {item.isFiltersMismatched && (
                    <div>{t("webhooks.recruitnow.filtersMismatch")}</div>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          );
        }

        // Check if there are any registrations at all
        if (item.registeredWebhookCount === 0) {
          return (
            <Badge variant="warning" size="medium">
              <Icons.WarningIcon fontSize={16} />
              {t("webhooks.recruitnow.status.noActualRegistration")}
            </Badge>
          );
        }

        // Check for mismatch
        if (item.isMismatched) {
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Badge variant="warning" size="medium">
                    <Icons.WarningIcon fontSize={16} />
                    {t("webhooks.recruitnow.status.mismatch")}
                  </Badge>
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-md">
                <div className="flex flex-col gap-1">
                  {item.isSecretMismatched && (
                    <div>{t("webhooks.recruitnow.secretMismatch")}</div>
                  )}
                  {item.isUriMismatched && (
                    <div>{t("webhooks.recruitnow.uriMismatch")}</div>
                  )}
                  {item.isFiltersMismatched && (
                    <div>{t("webhooks.recruitnow.filtersMismatch")}</div>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          );
        }

        // Client registration has no matching registration (by webhookId)
        if (!item.hasMatchingRegistration) {
          return (
            <Badge variant="warning" size="medium">
              <Icons.WarningIcon fontSize={16} />
              {t("webhooks.recruitnow.status.noActualRegistration")}
            </Badge>
          );
        }

        // Success: has webhook ID, matching registration, matching secret, and no errors
        return (
          <Badge variant="success" size="medium">
            <Icons.CheckCircleIcon fontSize={16} />
            {t("common.ok")}
          </Badge>
        );
      },
    },
  ];
}
